import { useEffect, useMemo, useRef, useState } from 'react';
import { Xmark } from '@peacecover/icon';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import type { GetBettieProactiveMessagesResponse } from '../../../domains/bettie/services/bettieService';
import { attachJosa } from '../../../utils/bettieUtils';
import './BettieMissedNotePopup.css';

type ProactiveMessage = GetBettieProactiveMessagesResponse['proactiveMessages'][number];

interface BettieMissedNotePopupProps {
  nickname?: string | null;
  thumbnailUrl?: string | null;
  proactiveMessage?: ProactiveMessage;
  disabled?: boolean;
  isOutOfPoints?: boolean;
  isSubmitting?: boolean;
  position?: 'fixed' | 'absolute';
  onReply: (content: string) => Promise<void> | void;
  onLater: () => Promise<void> | void;
  onDislike: () => Promise<void> | void;
}

const BettieMissedNotePopup = ({
  nickname,
  thumbnailUrl,
  proactiveMessage,
  disabled = false,
  isOutOfPoints = false,
  isSubmitting = false,
  position = 'fixed',
  onReply,
  onLater,
  onDislike
}: BettieMissedNotePopupProps) => {
  const [message, setMessage] = useState('');
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const memberName = nickname?.trim() || '';

  const titleText = useMemo(() => {
    if (!memberName) return '먼저 메시지를 보냈어요';
    return `${attachJosa(memberName, '이/가')} 먼저 메시지를 보냈어요`;
  }, [memberName]);

  const placeholder = useMemo(() => {
    if (disabled) return '지금은 답장을 보낼 수 없어요';
    if (isSubmitting) return '답장을 보내는 중이에요';
    if (memberName) return `${memberName}에게 답장하기`;
    return '답장하기';
  }, [disabled, isSubmitting, memberName]);

  useEffect(() => {
    if (disabled || isSubmitting) return;

    const focusTimer = window.setTimeout(() => {
      messageInputRef.current?.focus();
    }, 60);

    return () => window.clearTimeout(focusTimer);
  }, [disabled, isSubmitting]);

  if (!proactiveMessage) return null;

  return (
    <div
      className={`${position} inset-0 z-[1100] overflow-y-auto px-[16px] pb-[24px] pt-[calc(env(safe-area-inset-top)+72px)]`}
    >
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.42)] backdrop-blur-[10px]" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative mx-auto w-full max-w-[430px] rounded-[20px] border border-surface-lv2 bg-background-elevated px-[20px] py-[24px] shadow-[0_16px_50px_rgba(15,23,42,0.18)]"
      >
        <div className="flex items-start justify-between gap-[12px]">
          <div className="pt-[4px] text-[16px] font-bold leading-[20px]">
            <p>
              {memberName ? (
                <>
                  <span className="text-primary-light dark:text-primary-dark">
                    {attachJosa(memberName, '이/가')}
                  </span>
                  <span className="text-text-main-lv2"> 먼저 메시지를 보냈어요</span>
                </>
              ) : (
                <span className="text-text-main-lv2">{titleText}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            className="flex h-[28px] w-[28px] items-center justify-center rounded-[10px] text-[20px] font-semibold leading-none text-text-main-lv1"
            onClick={() => onLater()}
          >
            <Xmark size={20} className="text-text-main-lv1" />
          </button>
        </div>

        <div className="mt-[18px] flex items-center gap-[18px] px-[12px] py-[12px]">
          <div className="missed-note-avatar-float flex-shrink-0 self-center">
            <ProfileAvatar
              thumbnailUrl={thumbnailUrl || undefined}
              widthAndHeight="56px"
              svgWidthAndHeight="30px"
              className="flex-shrink-0"
            />
          </div>
          <div className="missed-note-bubble-float min-w-0 flex-1">
            <div className="relative">
              <div className="missed-note-bubble rounded-[20px] border border-[rgba(255,255,255,0.72)] bg-[#FFEFF7] px-[14px] py-[12px] dark:border-white/10 dark:bg-[#2A3145]">
                <p className="text-[14px] font-medium leading-[18px] text-text-main-lv1 whitespace-pre-wrap break-words">
                  {proactiveMessage.content}
                </p>
              </div>
              <div className="missed-note-bubble-tail-side h-[16px] w-[16px] rounded-[4px] border-l border-b border-[rgba(255,255,255,0.52)] bg-[#FFEFF7] dark:border-white/10 dark:bg-[#2A3145]" />
            </div>
          </div>
        </div>

        <div className="mt-[18px] flex flex-col gap-[8px]">
          <div className="flex items-center gap-[4px]">
            <textarea
              ref={messageInputRef}
              value={message}
              rows={1}
              maxLength={300}
              disabled={disabled || isSubmitting}
              placeholder={placeholder}
              className="h-[42px] flex-1 resize-none rounded-[12px] bg-secondary-light px-[12px] py-[11px] text-[14px] leading-[18px] text-text-main-lv1 outline-none placeholder:text-placeholder-light dark:bg-secondary-dark dark:placeholder:text-placeholder-dark"
              onChange={(event) => setMessage(event.target.value)}
            />
            <button
              type="button"
              disabled={!message.trim() || disabled || isSubmitting}
              onClick={() => onReply(message.trim())}
              className="flex h-[42px] min-w-[64px] items-center justify-center rounded-[12px] bg-primary-light px-[14px] text-[14px] font-semibold whitespace-nowrap text-pure-white transition-opacity dark:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-45"
            >
              보내기
            </button>
          </div>
          {isOutOfPoints ? (
            <p className="pt-[4px] text-center text-[12px] leading-[16px] text-text-main-lv3">
              하트가 부족하여 전송시 광고가 재생될 예정입니다.
            </p>
          ) : null}
          <div className="pb-[10px] pt-[18px]">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onDislike()}
              className="mx-auto block text-[12px] font-medium leading-[16px] text-text-main-lv4 underline decoration-[1px] underline-offset-[4px] disabled:cursor-not-allowed disabled:opacity-45"
            >
              이런 메시지는 제 취향이 아니에요
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BettieMissedNotePopup;
